import React, { useState, useRef, useEffect } from 'react';
import {
    Mail, Save, RotateCcw, CheckCircle, Loader2, Send,
    Eye, EyeOff, User, Clock, ArrowRight
} from 'lucide-react';
import {
    EMAIL_NEWSLETTER_TEMPLATES,
    EMAIL_TONE_OPTIONS,
    EMAIL_CTA_OPTIONS,
    EMAIL_QUICK_ACTIONS
} from '../../../ai/prompts/contentPrompts';

const validateEmailNewsletter = (content) => ({ isValid: true, errors: [], warnings: [] });

// Re-export for use in ContentCreationModal
export { EMAIL_NEWSLETTER_TEMPLATES, EMAIL_TONE_OPTIONS, EMAIL_CTA_OPTIONS, EMAIL_QUICK_ACTIONS };

// Email Newsletter Preview Component
const EmailNewsletterPreview = ({
    content = null,
    subjectLine = null,
    previewText = null,
    isStreaming = false,
    isEditable = false,
    onContentChange = null,
    hasUnsavedChanges = false,
    onSave = null,
    onSaveToVault = null,
    onStartOver = null,
    isSavingToVault = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isDraggingText, setIsDraggingText] = useState(false);
    const [showPreview, setShowPreview] = useState(true);
    const textareaRef = useRef(null);
    const textDragCounterRef = useRef(0);

    const defaultContent = `**Subject:** The Future of AI in Podcasting Is Here

**Preview:** Discover how AI is revolutionizing podcast production and what it means for creators like you.

Hey [First Name],

I just finished an incredible conversation with a tech innovator that I can't wait to share with you.

In our latest episode, we dove deep into how artificial intelligence is transforming podcast production. Here's what you'll learn:

**3 Game-Changing Insights:**

1. **Time Savings** - AI tools can cut your editing time by 90%, giving you more time to focus on content

2. **Quality Boost** - Automated enhancement ensures consistent, professional sound across every episode

3. **Content Repurposing** - Turn one episode into dozens of social posts, blog articles, and more

**My Favorite Moment:**

> "AI isn't replacing podcasters - it's amplifying their creativity and giving them superpowers."

**This Week's Challenge:**

Try using one AI tool in your workflow this week. Even something simple like automated transcription can save hours.

**Listen to the full episode here:** [Listen Now]

Cheers,
The PodGuestly Team

P.S. - Hit reply and let me know what AI tools you're already using. I read every response!`;

    const displayContent = content || defaultContent;

    // Parse subject and preview from content
    const parsedSubject = () => {
        const match = displayContent.match(/\*\*Subject:\*\*\s*(.+?)(?:\n|$)/i);
        return match ? match[1].trim() : subjectLine || 'Newsletter';
    };

    const parsedPreview = () => {
        const match = displayContent.match(/\*\*Preview:\*\*\s*(.+?)(?:\n\n|$)/i);
        return match ? match[1].trim() : previewText || '';
    };

    // Parse inline markdown
    const parseInlineMarkdown = (text) => {
        if (!text) return text;
        const parts = [];
        let remaining = text;
        let keyIndex = 0;

        while (remaining.length > 0) {
            const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
            if (boldMatch) {
                parts.push(<strong key={`bold-${keyIndex++}`} className="font-bold text-gray-900">{boldMatch[1]}</strong>);
                remaining = remaining.slice(boldMatch[0].length);
                continue;
            }
            const italicMatch = remaining.match(/^[*_]([^*_]+?)[*_]/);
            if (italicMatch) {
                parts.push(<em key={`italic-${keyIndex++}`} className="italic">{italicMatch[1]}</em>);
                remaining = remaining.slice(italicMatch[0].length);
                continue;
            }
            const linkMatch = remaining.match(/^\[([^\]]+)\]/);
            if (linkMatch) {
                parts.push(<span key={`link-${keyIndex++}`} className="text-indigo-600 font-medium underline cursor-pointer">{linkMatch[1]}</span>);
                remaining = remaining.slice(linkMatch[0].length);
                continue;
            }
            if (parts.length > 0 && typeof parts[parts.length - 1] === 'string') {
                parts[parts.length - 1] += remaining[0];
            } else {
                parts.push(remaining[0]);
            }
            remaining = remaining.slice(1);
        }
        return parts.map((part, idx) => typeof part === 'string' ? <span key={`text-${idx}`}>{part}</span> : part);
    };

    // Format content with markdown rendering
    const formatContent = (text) => {
        if (!text) return null;
        const lines = text.split('\n');
        const elements = [];
        let inList = false;
        let listItems = [];

        const flushList = () => {
            if (listItems.length > 0) {
                elements.push(<ul key={`list-${elements.length}`} className="space-y-2 mb-4 ml-1">{listItems}</ul>);
                listItems = [];
                inList = false;
            }
        };

        lines.forEach((line, index) => {
            const trimmedLine = line.trim();

            // Skip subject and preview lines in body
            if (trimmedLine.startsWith('**Subject:**') || trimmedLine.startsWith('**Preview:**')) {
                return;
            }

            // H1 equivalent (first line or explicit header)
            if (trimmedLine.startsWith('# ') && !trimmedLine.startsWith('## ')) {
                flushList();
                elements.push(<h1 key={index} className="text-2xl font-bold text-gray-900 mb-4">{parseInlineMarkdown(trimmedLine.slice(2))}</h1>);
                return;
            }
            // H2 equivalent
            if (trimmedLine.startsWith('## ') && !trimmedLine.startsWith('### ')) {
                flushList();
                elements.push(<h2 key={index} className="text-xl font-bold text-gray-900 mb-3 mt-6">{parseInlineMarkdown(trimmedLine.slice(3))}</h2>);
                return;
            }

            // Bullet points
            const bulletMatch = trimmedLine.match(/^[·\-\*]\s+(.+)/);
            if (bulletMatch) {
                inList = true;
                listItems.push(<li key={index} className="flex items-start gap-3 text-gray-700 leading-relaxed"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2.5 flex-shrink-0"></span><span className="flex-1">{parseInlineMarkdown(bulletMatch[1])}</span></li>);
                return;
            }

            // Numbered list
            const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)/);
            if (numberedMatch) {
                inList = true;
                listItems.push(<li key={index} className="flex items-start gap-3 text-gray-700 leading-relaxed"><span className="text-indigo-600 font-semibold min-w-[24px]">{numberedMatch[1]}.</span><span className="flex-1">{parseInlineMarkdown(numberedMatch[2])}</span></li>);
                return;
            }

            // Blockquote
            if (trimmedLine.startsWith('> ')) {
                flushList();
                elements.push(<blockquote key={index} className="my-4 pl-4 pr-4 py-3 border-l-4 border-indigo-500 bg-indigo-50 rounded-r-lg"><p className="text-gray-700 italic leading-relaxed">{parseInlineMarkdown(trimmedLine.slice(2))}</p></blockquote>);
                return;
            }

            // Horizontal rule
            if (trimmedLine === '---' || trimmedLine === '***' || trimmedLine === '___') {
                flushList();
                elements.push(<hr key={index} className="my-6 border-gray-200" />);
                return;
            }

            // P.S. styling
            if (trimmedLine.match(/^P\.?S\.?/i)) {
                flushList();
                elements.push(<p key={index} className="text-gray-600 text-sm mt-4 italic">{parseInlineMarkdown(trimmedLine)}</p>);
                return;
            }

            // Regular paragraph
            if (trimmedLine) {
                flushList();
                elements.push(<p key={index} className="text-gray-700 leading-relaxed mb-4">{parseInlineMarkdown(trimmedLine)}</p>);
                return;
            }

            flushList();
        });

        flushList();
        return elements;
    };

    const handleTextChange = (e) => {
        if (onContentChange) onContentChange(e.target.value);
    };

    const handleContentClick = () => {
        if (isEditable && !isStreaming) setIsEditing(true);
    };

    const handleBlur = () => setIsEditing(false);

    useEffect(() => {
        if (isEditing && textareaRef.current) textareaRef.current.focus();
    }, [isEditing]);

    const handleTextDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current++;
        if (textDragCounterRef.current === 1) setIsDraggingText(true);
    };

    const handleTextDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current--;
        if (textDragCounterRef.current === 0) setIsDraggingText(false);
    };

    const handleTextDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleTextDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current = 0;
        setIsDraggingText(false);

        if (e.dataTransfer.types.includes('application/json')) {
            try {
                const jsonData = e.dataTransfer.getData('application/json');
                const artifact = JSON.parse(jsonData);
                if (artifact?.type === 'vault-asset' && artifact.assetType === 'text' && artifact.content) {
                    if (onContentChange) onContentChange(artifact.content);
                }
            } catch (err) { console.error('Failed to parse vault asset:', err); }
        }

        if (e.dataTransfer.types.includes('text/plain')) {
            const text = e.dataTransfer.getData('text/plain');
            if (text && onContentChange) onContentChange(text);
        }
    };

    // Extract subject for display
    const subjectMatch = displayContent.match(/\*\*Subject:\*\*\s*(.+?)(?:\n|$)/i);
    const displaySubject = subjectMatch ? subjectMatch[1].trim() : 'Newsletter';

    return (
        <div className="flex flex-col h-full bg-slate-900 font-['Inter']">
            {/* Action Bar */}
            {isEditable && content && (
                <div className="w-full px-4 py-3 border-b border-slate-700/50 bg-slate-800/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                <Mail size={14} />
                                <span>{displayContent.split(/\s+/).length.toLocaleString()} words</span>
                            </div>
                            <div className="w-px h-4 bg-slate-600" />
                            {saveSuccess ? (
                                <><CheckCircle size={14} className="text-green-500" /><span className="text-xs text-green-400 font-medium">Saved!</span></>
                            ) : hasUnsavedChanges ? (
                                <><div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" /><span className="text-xs text-amber-400 font-medium">Unsaved</span></>
                            ) : (
                                <><CheckCircle size={14} className="text-green-500" /><span className="text-xs text-green-400 font-medium">Ready</span></>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setShowPreview(!showPreview)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:bg-slate-700 hover:text-slate-200 transition-all">
                                {showPreview ? <><EyeOff size={14} />Hide Preview</> : <><Eye size={14} />Show Preview</>}
                            </button>
                            <button onClick={onStartOver} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:bg-slate-700 hover:text-slate-200 transition-all">
                                <RotateCcw size={14} />Start Over
                            </button>
                            <button onClick={async () => { if (onSaveToVault) { const success = await onSaveToVault(); if (success) { setSaveSuccess(true); setTimeout(() => setSaveSuccess(false), 2000); } } }} disabled={isSavingToVault || !content} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isSavingToVault ? 'bg-slate-700 text-slate-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-sm'}`}>
                                {isSavingToVault ? <><Loader2 size={14} className="animate-spin" />Saving...</> : <><Save size={14} />Save</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Client Frame */}
            <div className="flex-1 flex flex-col min-h-0">
                {/* Email Client Header */}
                <div className="flex-shrink-0 bg-gradient-to-b from-slate-700 to-slate-800 rounded-t-xl px-4 py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                                <Mail size={16} className="text-white" />
                            </div>
                            <span className="text-sm font-medium text-slate-200">Email Newsletter</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button className="p-2 text-slate-400 hover:text-slate-200 transition-colors rounded-lg hover:bg-slate-700">
                                <Send size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto bg-white rounded-b-xl" onDragEnter={handleTextDragEnter} onDragLeave={handleTextDragLeave} onDragOver={handleTextDragOver} onDrop={handleTextDrop}>
                    {isEditing && isEditable && !isStreaming ? (
                        <textarea ref={textareaRef} value={displayContent} onChange={handleTextChange} onBlur={handleBlur} className={`w-full h-full min-h-[500px] p-8 text-sm text-gray-900 leading-relaxed whitespace-pre-wrap resize-none border-0 outline-none font-mono ${isDraggingText ? 'bg-indigo-50 ring-2 ring-indigo-300' : ''}`} placeholder="Write your email newsletter here..." />
                    ) : (
                        <div onClick={handleContentClick} className={`p-8 max-w-2xl mx-auto transition-all ${isDraggingText ? 'bg-indigo-50 min-h-[300px] flex items-center justify-center' : ''} ${isEditable && !isStreaming ? 'cursor-text' : ''}`}>
                            {isDraggingText ? (
                                <div className="text-center">
                                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-indigo-100 flex items-center justify-center"><Mail size={24} className="text-indigo-600" /></div>
                                    <span className="text-indigo-600 font-semibold text-base">Drop text content here</span>
                                    <p className="text-indigo-400 text-xs mt-1">Release to insert text</p>
                                </div>
                            ) : (
                                <div className="email-content">
                                    {/* Subject Line Display */}
                                    {showPreview && (
                                        <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                                                <Mail size={12} />
                                                <span>Subject Line</span>
                                            </div>
                                            <p className="text-lg font-semibold text-gray-900">{displaySubject}</p>
                                        </div>
                                    )}

                                    {/* Email Body */}
                                    <article className="prose prose-indigo max-w-none">
                                        {formatContent(displayContent)}
                                        {isStreaming && <span className="inline-block w-1.5 h-5 bg-indigo-500 animate-pulse ml-0.5" />}
                                    </article>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export { EmailNewsletterPreview };
export default EmailNewsletterPreview;